import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import "./styles/base.css";
import "./styles/app.css";
import "./styles/direction.css";
import "./styles/pro.css";
import { applyStoredTheme } from "./lib/theme";
import { readPref, writePref } from "./lib/viewPrefs";
import { exampleRequested, installExample } from "./lib/example/mode";
import { followPinchZoom } from "./lib/pinchZoom";

const root = document.getElementById("root");
if (root === null) throw new Error("#root is missing from index.html");

/* ⚠️ THE ONE THING THAT HAPPENS BEFORE THE APP MOUNTS, AND ONLY IN DEVELOPMENT.
 *
 * The dev render harness (`dev/stub.ts`) exists because the rail, the account disclosure and both
 * themes had never been LOOKED AT signed in — signing in for real needs the owner's password, and
 * a password is his to type, never Claude's. It installs a session and a `fetch` interceptor fed
 * by a git-ignored dump of the real database, so every query in this app runs its real code.
 *
 * 🔴 IT CANNOT REACH PRODUCTION, AND THAT IS CHECKED RATHER THAN CLAIMED. `import.meta.env.DEV`
 * is replaced with the literal `false` at build time, so this whole branch — and the dynamic
 * import inside it, and therefore the chunk — is deleted by the bundler. `npm run verify:stub`
 * greps `dist/` for the stub's marker and fails the build if it is there.
 */
async function boot() {
  // W81 — the coach's stored Light / Dark / Auto, stamped before anything paints (the switch itself lives in the sidebar).
  // W140: read through viewPrefs — the SIGNED-IN COACH's choice (the session the auth client keeps names them, synchronously).
  applyStoredTheme(() => readPref("theme"), document.documentElement);
  if (import.meta.env.DEV) {
    // Dev-only, beside the stub: `?ht-theme=dark|light` writes the theme control's own key and
    // stamps the root, so a headless render can capture either theme without a click. Vite
    // replaces `import.meta.env.DEV` with `false` at build time, so this never ships.
    const theme = new URLSearchParams(window.location.search).get("ht-theme");
    // Same idea for the exercise library's view, so a headless render can capture the grid.
    const view = new URLSearchParams(window.location.search).get("ht-view");
    // `?ht-example` opens the example workspace in a headless render (the tab's flag, as the entry link sets it).
    if (new URLSearchParams(window.location.search).has("ht-example")) { try { sessionStorage.setItem("ht-example", "1"); } catch { /* none */ } }
    if (view === "grid" || view === "list") { writePref("shelfExercises", view); }
    if (theme === "dark" || theme === "light") {
      writePref("theme", theme); // a render without storage keeps the system theme
      document.documentElement.dataset.theme = theme;
    }
  }
  if (import.meta.env.DEV && new URLSearchParams(window.location.search).has("ht-stub")) {
    const stub = await import("./dev/stub");
    await stub.install();
    // W136 B2 S3r3 — the theme switch AGAIN, now that the stub has signed the coach in: since W140 the theme is the signed-in
    // coach's pref, so the write above (keyed `anon`, before the sign-in) lost to the coach's own and `?ht-theme=dark` rendered
    // LIGHT (two byte-identical stills, MEASURED 2026-10-05).
    const stubTheme = new URLSearchParams(window.location.search).get("ht-theme");
    if (stubTheme === "dark" || stubTheme === "light") {
      writePref("theme", stubTheme);
      document.documentElement.dataset.theme = stubTheme;
    }
  }
  /* THE EXAMPLE WORKSPACE (2026-09-28) — shipped, unlike the stub above. Only when this tab asked for it; it answers
     reads from a made-up world and refuses every write before it leaves the browser (`lib/example/mode.ts`). */
  if (exampleRequested()) await installExample();
  // 2026-10-18 — a pinch-zoomed page keeps every window inside what is visible (`lib/pinchZoom.ts`, William's still).
  followPinchZoom();
  createRoot(root!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void boot();
